/* ═══════════════════════════════════════════════════════════════
   ORG FEATURES — the flags the screens read about an org's optional
   features, from what GET /api/org/features answered (AuthContext). No
   React, no fetch.

   The analogue chart reader is an entitlement billed per disc, so a flag
   is only ever what the server said: never a default, never a guess. That
   cuts both ways. Until the server has answered, and when the lookup
   failed (fetchOrgFeatures then answers {}), nothing is granted, and
   nothing is said to be switched off either: an org that pays for the
   reader, told it was switched off on a slow or failed lookup, would have
   been told something untrue for the whole session (design 2026-10-06,
   billing review).
   ═══════════════════════════════════════════════════════════════ */

const isObj = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);

/**
 * The analogue chart reader's flags, from the features answer and whether
 * it has come for the org on screen (`loaded`, AuthContext's
 * featuresLoaded):
 *
 *   analogueEnabled      on: the server's own true
 *   analogueSwitchedOff  off: the server's own false, once it has answered
 *   analogueHasCharts    charts on file, on or off: the server's own true
 *   analogueUnknown      could not tell: it has answered, but not with a true
 *                        or false for the reader, which is what a failed
 *                        lookup's {} leaves
 *
 * Before the answer, or after a lookup that failed, the reader is neither
 * on nor off as far as any screen may say. A failed lookup is said as such,
 * so an org that pays for the reader is not left with no upload box and no
 * word of why; one still on its way says nothing. The last answer's own
 * true stands while the next is on its way, as the sidebar has always had
 * it: the doors check the entitlement themselves.
 */
export function analogueFlags(features, loaded) {
  const said = isObj(features) ? features : {};
  const answered = loaded === true;
  return {
    analogueEnabled: said.analogue_charts === true,
    analogueSwitchedOff: answered && said.analogue_charts === false,
    analogueHasCharts: said.analogue_has_charts === true,
    analogueUnknown: answered && typeof said.analogue_charts !== 'boolean',
  };
}
